/**
 * SelectLeagues — league downgrade/selection screen
 *
 * Shown instead of the normal dashboard whenever needsLeagueSelection is true
 * (see context/AuthContext.jsx) — a plan downgrade left the user with more
 * ACTIVE leagues than their new plan allows. JAI never auto-deactivates
 * anything; the user has to manually choose which league(s) to keep here.
 *
 * Lists every league (active + inactive) with a deactivate/reactivate
 * control. Once enough leagues are deactivated to get back within the plan's
 * limit, needsLeagueSelection clears server-side and this page redirects to
 * the normal app.
 */

import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { leaguesApi } from '../services/api'
import { useAuth } from '../context/AuthContext'
import { PLAN_LEAGUE_LIMIT } from '../config/planAccess'
import AppShell from '../components/AppShell'
import { V2, V2Page, V2Card, PrimaryBtn, SecondaryBtn, Notice } from '../components/JaiV2'
import { JAIMascot } from '../components/JAIMascot'

export default function SelectLeagues() {
  const { plan, planLoading, needsLeagueSelection, refreshPlan } = useAuth()
  const navigate = useNavigate()

  const [leagues, setLeagues] = useState([])
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId]   = useState(null)
  const [error, setError]     = useState('')

  const limit       = PLAN_LEAGUE_LIMIT[plan]
  const activeCount = leagues.filter(l => l.active).length
  const remaining   = limit !== null ? Math.max(0, activeCount - limit) : 0

  useEffect(() => {
    loadLeagues()
  }, [])

  // Once the block clears server-side (enough leagues deactivated), leave automatically
  useEffect(() => {
    if (!loading && !planLoading && !needsLeagueSelection) {
      navigate('/chat', { replace: true })
    }
  }, [needsLeagueSelection, planLoading, loading])

  const loadLeagues = async () => {
    setLoading(true)
    setError('')
    try {
      const res = await leaguesApi.getAll({ includeInactive: 1 })
      setLeagues(res.data.leagues || [])
    } catch {
      setError('Failed to load your leagues.')
    } finally {
      setLoading(false)
    }
  }

  const toggleLeague = async (league) => {
    setBusyId(league.id)
    setError('')
    try {
      if (league.active) {
        await leaguesApi.deactivate(league.id)
      } else {
        await leaguesApi.reactivate(league.id)
      }
      await loadLeagues()
      await refreshPlan()
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong — try again.')
    } finally {
      setBusyId(null)
    }
  }

  if (loading) {
    return (
      <AppShell>
        <div className="flex flex-col items-center justify-center h-full min-h-screen gap-3">
          <JAIMascot size={56} state="thinking" animate={true} />
          <div className="text-sm" style={{ color: V2.faint }}>Loading your leagues…</div>
        </div>
      </AppShell>
    )
  }

  return (
    <AppShell>
      <V2Page
        kicker="Plan change"
        title="Choose which leagues to keep"
        sub={
          limit !== null
            ? `Your plan allows ${limit} active league${limit === 1 ? '' : 's'}. You currently have ${activeCount} active — deactivate ${remaining} more to continue.`
            : 'Choose which leagues to keep active.'
        }
        maxWidth={720}
      >
        {error && (
          <Notice tone="error" style={{ marginTop: 0 }}>{error}</Notice>
        )}

        <Notice tone="info" style={{ marginTop: 0, marginBottom: 20 }}>Deactivated leagues are never deleted — your data is kept, and you can reactivate any of them later
          (as long as it fits your plan's limit).</Notice>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {leagues.map(league => (
            <V2Card key={league.id} pad="16px 20px">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14.5, fontWeight: 700, color: V2.navy }}>{league.name}</div>
                  <div style={{ fontSize: 12, color: V2.faint, marginTop: 2 }}>
                    {(league.sport || 'nba').toUpperCase()} · {league.season} season ·{' '}
                    <span style={{ color: league.active ? '#1A8754' : V2.faint, fontWeight: 600 }}>
                      {league.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
                {league.active ? (
                  <SecondaryBtn onClick={() => toggleLeague(league)} disabled={busyId === league.id}>
                    {busyId === league.id ? 'Deactivating…' : 'Deactivate'}
                  </SecondaryBtn>
                ) : (
                  <PrimaryBtn
                    onClick={() => toggleLeague(league)}
                    disabled={busyId === league.id || (limit !== null && activeCount >= limit)}
                  >
                    {busyId === league.id ? 'Reactivating…' : 'Reactivate'}
                  </PrimaryBtn>
                )}
              </div>
            </V2Card>
          ))}
        </div>
      </V2Page>
    </AppShell>
  )
}
